import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import {
  getAssistants,
  postAssistantAgent,
  putAssistantAgent,
  deleteAssistantAgent,
} from "../service";
import { ASSISTANT } from "../Assistant";
import AssistantAgentsTable from "./AssistantAgentsTable";
import { AssistantAgentCreate } from "./AssistantAgentCreate";
import { AssistantAgentEdit } from "./AssistantAgentEdit";
import { TransferAgentDelete } from "./TransferAgentDelete";

export function AssistantTransferAgents(props) {
  const { transferSettings, assistantId, syncAssistant, workspaceId, locked } =
    props;
  const assistantAgents = transferSettings.assistantAgents ?? [];

  const [assistants, setAssistants] = useState([]);
  const [open, setOpen] = useState({ mode: null, item: null, index: -1 });

  useEffect(() => {
    let isMounted = true;
    async function fetchAssistants() {
      try {
        // project === workspace in this product; getAssistants is scoped by projectId
        const response = await getAssistants({ projectId: workspaceId });
        if (isMounted && response.status === 200) {
          setAssistants(response.data.assistants ?? []);
        }
      } catch (e) {
        console.error("Error fetching assistants for transfer targets");
      }
    }
    fetchAssistants();
    return () => {
      isMounted = false;
    };
  }, [workspaceId]);

  // Only voice assistants are valid internal transfer targets, and an
  // assistant should not be able to transfer to itself.
  const transferableAssistants = useMemo(() => {
    return assistants.filter(
      (assistant) =>
        assistant.assistantType === ASSISTANT.VOICE &&
        assistant._id !== assistantId &&
        !assistant?.hidden
    );
  }, [assistants, assistantId]);

  const assistantOptions = useMemo(() => {
    return transferableAssistants.map((assistant, idx) => ({
      id: idx,
      value: assistant._id,
      label: assistant.name,
    }));
  }, [transferableAssistants]);

  const assistantNameById = useMemo(() => {
    return assistants.reduce((acc, assistant) => {
      acc[assistant._id] = assistant.name;
      return acc;
    }, {});
  }, [assistants]);

  const onClose = () => setOpen({ mode: null, item: null, index: -1 });

  const onAgentEdit = (item, index) => setOpen({ mode: "edit", item, index });
  const onAgentDelete = (item, index) =>
    setOpen({ mode: "delete", item, index });

  const syncAssistantAgents = (updatedAssistantAgents) => {
    syncAssistant((prevAssistant) => {
      return {
        ...prevAssistant,
        transferSettings: {
          ...prevAssistant.transferSettings,
          assistantAgents: updatedAssistantAgents,
        },
      };
    });
  };

  const createAssistantAgent = async (
    data,
    stopLoading,
    startLoading,
    onSuccess
  ) => {
    startLoading();
    message.loading("Adding Assistant Agent");
    try {
      const response = await postAssistantAgent({
        workspaceId,
        assistantId,
        payload: data,
      });
      if (response.status >= 200 && response.status < 300) {
        message.destroy();
        message.success("Assistant agent added");
        stopLoading();
        syncAssistantAgents(response.data);
        onSuccess();
      }
    } catch (e) {
      stopLoading();
      message.destroy();
      message.error(
        e.response?.data?.msg ||
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Failed to add assistant agent"
      );
      console.error("Error creating assistant agent");
      throw e;
    }
  };

  const updateAssistantAgent = async (updatedAgent) => {
    message.loading("Updating Assistant Agent");
    try {
      const response = await putAssistantAgent({
        workspaceId,
        assistantId,
        agentId: open.item._id,
        payload: updatedAgent,
      });
      if (response.status >= 200 && response.status < 300) {
        message.destroy();
        message.success("Assistant agent updated");
        syncAssistantAgents(response.data);
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response?.data?.msg ||
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Failed to update assistant agent"
      );
      console.error("Error updating assistant agent");
      throw e;
    }
  };

  const removeAssistantAgent = async () => {
    message.loading("Deleting Assistant Agent");
    try {
      const response = await deleteAssistantAgent({
        workspaceId,
        assistantId,
        agentId: open.item._id,
      });
      if (response.status >= 200 && response.status < 300) {
        message.destroy();
        message.success("Assistant agent deleted");
        syncAssistantAgents(response.data);
      }
    } catch (e) {
      message.destroy();
      message.error(
        e.response?.data?.msg ||
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Failed to delete assistant agent"
      );
      console.error("Error deleting assistant agent");
      throw e;
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-y-2 border rounded-lg m-4">
        <div className="px-6 py-4 bg-gray-50 rounded-t-lg w-full">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <p className="font-semibold text-base">Assistant Agents</p>
              <p className="text-sm text-gray-500">
                Transfer the call to an AI Assistant
              </p>
            </div>
            {!locked && (
              <AssistantAgentCreate
                createAssistantAgent={createAssistantAgent}
                assistantOptions={assistantOptions}
              />
            )}
          </div>
        </div>
        <div className="w-full min-h-96 overflow-auto px-6">
          <AssistantAgentsTable
            agents={assistantAgents}
            assistantNameById={assistantNameById}
            onAgentEdit={onAgentEdit}
            onAgentDelete={onAgentDelete}
            showActions={!locked}
          />
        </div>
      </div>
      {open.mode === "edit" && !locked && (
        <AssistantAgentEdit
          agent={open.item}
          updateAssistantAgent={updateAssistantAgent}
          assistantOptions={assistantOptions}
          open={true}
          onClose={onClose}
        />
      )}
      {open.mode === "delete" && !locked && (
        <TransferAgentDelete
          deleteAgent={removeAssistantAgent}
          open={true}
          onClose={onClose}
        />
      )}
    </>
  );
}
